import { useCallback, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronDown, Send, Check, HelpCircle, X, Loader2, FileText, Sparkles, LifeBuoy } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { CLASSIFICATION_META } from "@/lib/portal";
import { capi } from "@/lib/customer";

const QUESTIONS = [
  { key: "property", q: "Will you acquire real estate?" },
  { key: "business", q: "Will you establish a business?" },
  { key: "family", q: "Are family dependents joining you?" },
];

const STATUS_CHIP = {
  not_started: { label: "Not explored", color: "#94A3B8" },
  in_progress: { label: "Exploring", color: "#60A5FA" },
  needs_review: { label: "Discuss with adviser", color: "#FB923C" },
  ready: { label: "Ready", color: "#34D399" },
};

const ANSWERS = [
  { key: "yes", label: "Yes", icon: Check, color: "#34D399" },
  { key: "maybe", label: "Maybe", icon: HelpCircle, color: "#FBBF24" },
  { key: "no", label: "No", icon: X, color: "#F87171" },
];

function visibleNodes(nodes, answers) {
  return (nodes || []).filter((n) => n.classification !== "conditional" || !!answers?.[n.branch]);
}

export default function ServerJourneyView({ journey, onBack, onUpdateAnswer, onJourneyUpdate, onCoordinate }) {
  const [expanded, setExpanded] = useState(null);
  const [reqs, setReqs] = useState({}); // node_id -> [requirements]
  const [loading, setLoading] = useState({}); // node_id -> bool

  const loadReqs = useCallback(async (nodeId) => {
    if (reqs[nodeId]) return;
    setLoading((l) => ({ ...l, [nodeId]: true }));
    try {
      const { data } = await capi.get(`/customer/journeys/${journey.id}/nodes/${nodeId}/requirements`);
      setReqs((r) => ({ ...r, [nodeId]: data.requirements }));
    } catch {
      setReqs((r) => ({ ...r, [nodeId]: [] }));
    } finally {
      setLoading((l) => ({ ...l, [nodeId]: false }));
    }
  }, [journey?.id, reqs]);

  const toggleExpand = (nodeId) => {
    const next = expanded === nodeId ? null : nodeId;
    setExpanded(next);
    if (next) loadReqs(next);
  };

  const answer = async (nodeId, req, ans) => {
    // optimistic local update
    setReqs((r) => ({
      ...r,
      [nodeId]: (r[nodeId] || []).map((x) => (x.id === req.id ? { ...x, answer: ans } : x)),
    }));
    try {
      const { data } = await capi.patch(`/customer/journeys/${journey.id}/nodes/${nodeId}/assess`, {
        requirement_id: req.id,
        label: req.label,
        answer: ans,
      });
      onJourneyUpdate?.(data);
    } catch { /* keep optimistic */ }
  };

  if (!journey) return null;
  const nodes = visibleNodes(journey.nodes, journey.answers);
  const readiness = journey.readiness || { total_steps: nodes.length, ready_steps: 0, strengths: 0, flags: [] };
  const progress = journey.progress ?? 0;
  const flags = readiness.flags || [];

  const counts = (node) => {
    const list = reqs[node.id] || [];
    const y = list.filter((r) => r.answer === "yes").length;
    const f = list.filter((r) => r.answer === "maybe" || r.answer === "no").length;
    return { y, f, hasData: list.length > 0 };
  };

  return (
    <div className="mx-auto max-w-lg px-4 pb-32 pt-4">
      <button data-testid="journey-back-btn" onClick={onBack} className="mb-3 flex items-center gap-1 text-xs font-medium text-slate-300">
        <ChevronLeft size={15} /> My World
      </button>

      {/* objective + readiness */}
      <div
        data-testid="journey-objective-banner"
        className="rounded-2xl border p-4"
        style={{ borderColor: "rgba(226,194,117,0.3)", background: "linear-gradient(135deg, rgba(226,194,117,0.12), rgba(13,17,26,0.4))" }}
      >
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400/90">Your Objective</p>
        <h2 className="mt-1 flex items-center gap-2 text-xl font-semibold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
          <span className="text-2xl">{journey.flag}</span> {journey.objective}
        </h2>
        <p className="mt-1 text-xs text-slate-300">{journey.program}</p>

        <div className="mt-4 flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/10">
            <motion.div className="h-full rounded-full bg-emerald-400" initial={{ width: 0 }} animate={{ width: `${progress}%` }} data-testid="journey-progress-bar" />
          </div>
          <span className="font-mono text-sm font-semibold text-emerald-300" data-testid="journey-progress-value">{progress}%</span>
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-300">
          <span data-testid="journey-ready-count">{readiness.ready_steps}/{readiness.total_steps} steps ready</span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-emerald-300"><Sparkles size={12} /> {readiness.strengths} strength{readiness.strengths === 1 ? "" : "s"}</span>
            <span className="flex items-center gap-1 text-amber-300" data-testid="journey-flag-count"><LifeBuoy size={12} /> {flags.length} to discuss</span>
          </div>
        </div>
      </div>

      {/* discovery helper */}
      <div className="mt-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <p className="text-[12px] leading-relaxed text-slate-300">
          This is a guided discovery. Open each step, read what it involves, and honestly mark whether you have it.
          Anything you mark <span className="font-semibold text-amber-300">Maybe</span> or <span className="font-semibold text-red-300">No</span> is where your adviser will review your situation and guide you on the best approach.
        </p>
      </div>

      {/* branching questions */}
      <div className="mt-4 rounded-2xl border border-white/[0.06] bg-white/[0.03] p-4">
        <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Tailor your pathway</p>
        <div className="mt-3 space-y-3">
          {QUESTIONS.map((item) => (
            <div key={item.key} className="flex items-center justify-between">
              <span className="text-sm text-slate-200">{item.q}</span>
              <Switch data-testid={`journey-toggle-${item.key}`} checked={!!journey.answers?.[item.key]} onCheckedChange={(v) => onUpdateAnswer(item.key, v)} />
            </div>
          ))}
        </div>
      </div>

      {/* steps */}
      <div className="mt-6">
        <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400/90">Your {journey.country_name} Journey</p>
        <div className="space-y-2.5">
          {nodes.map((node) => {
            const cls = CLASSIFICATION_META[node.classification] || CLASSIFICATION_META.optional;
            const chip = STATUS_CHIP[node.status] || STATUS_CHIP.not_started;
            const isOpen = expanded === node.id;
            const c = counts(node);
            return (
              <div key={node.id} data-testid={`journey-node-${node.id}`} className="overflow-hidden rounded-xl border" style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.03)" }}>
                <button
                  data-testid={`journey-node-toggle-${node.id}`}
                  onClick={() => toggleExpand(node.id)}
                  className="flex w-full items-start gap-3 p-3.5 text-left"
                >
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 font-mono text-[10px] font-bold" style={{ borderColor: chip.color, color: chip.color }}>
                    {node.id}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-slate-100">{node.title}</h3>
                      <span className="rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider" style={{ background: cls.bg, color: cls.color, border: `1px solid ${cls.border}` }}>{cls.label}</span>
                    </div>
                    <p className="mt-1 text-xs leading-snug text-slate-400">{node.summary}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ color: chip.color, background: `${chip.color}1f` }} data-testid={`journey-node-chip-${node.id}`}>{chip.label}</span>
                      {c.hasData && (c.y > 0 || c.f > 0) && (
                        <span className="text-[10px] text-slate-400">{c.y} ✓ · {c.f} to discuss</span>
                      )}
                    </div>
                  </div>
                  <ChevronDown size={16} className={`mt-1 shrink-0 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>

                {isOpen && (
                  <div className="border-t border-white/[0.06] p-3.5 pt-3">
                    {loading[node.id] ? (
                      <div className="flex items-center gap-2 py-4 text-xs text-slate-400"><Loader2 size={14} className="animate-spin" /> Preparing your checklist…</div>
                    ) : (reqs[node.id] || []).length === 0 ? (
                      <p className="py-3 text-xs text-slate-400">No checklist available for this step yet.</p>
                    ) : (
                      <div className="space-y-3">
                        {(reqs[node.id] || []).map((req) => (
                          <div key={req.id} data-testid={`journey-req-${req.id}`} className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-3">
                            <p className="text-[13px] font-semibold text-slate-100">{req.label}</p>
                            {req.what && <p className="mt-1 text-[12px] leading-snug text-slate-300">{req.what}</p>}
                            {req.why && <p className="mt-1 text-[11px] italic leading-snug text-slate-400">Why it matters: {req.why}</p>}
                            {req.documents && (
                              <p className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-400"><FileText size={11} /> {req.documents}</p>
                            )}
                            <div className="mt-2.5 grid grid-cols-3 gap-1.5">
                              {ANSWERS.map((a) => {
                                const active = req.answer === a.key;
                                const Icon = a.icon;
                                return (
                                  <button
                                    key={a.key}
                                    data-testid={`journey-answer-${req.id}-${a.key}`}
                                    onClick={() => answer(node.id, req, a.key)}
                                    className="flex items-center justify-center gap-1 rounded-lg border py-1.5 text-[11px] font-semibold transition-colors"
                                    style={{
                                      borderColor: active ? a.color : "rgba(255,255,255,0.1)",
                                      background: active ? `${a.color}22` : "transparent",
                                      color: active ? a.color : "#94A3B8",
                                    }}
                                  >
                                    <Icon size={12} /> {a.label}
                                  </button>
                                );
                              })}
                            </div>
                            {(req.answer === "maybe" || req.answer === "no") && (
                              <p className="mt-2 flex items-center gap-1 text-[11px] text-amber-300"><LifeBuoy size={11} /> Your adviser will review this with you and advise on the best approach.</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* sticky CTA */}
      <div className="fixed inset-x-0 bottom-[60px] z-20 mx-auto max-w-lg border-t border-white/10 bg-[#07090E]/95 px-4 py-3 backdrop-blur-md">
        {flags.length > 0 && (
          <p className="mb-2 text-center text-[11px] text-amber-300">{flags.length} item{flags.length > 1 ? "s" : ""} to discuss will be shared with your consultant</p>
        )}
        <button
          data-testid="btn-request-more-info"
          onClick={() => onCoordinate?.(journey)}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-amber-300 py-3.5 text-sm font-semibold text-[#07090E] transition-transform active:scale-[0.98]"
        >
          <Send size={16} /> Request Coordination From My Adviser
        </button>
      </div>
    </div>
  );
}
